import { ContentfulImage } from "@2u-landing-pages/components/ContentfulImage";
import { ContentLayout } from "@2u-landing-pages/components/ContentLayout";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { Bento } from "@2u-landing-pages/templates/Bento";
import { Form } from "@2u-landing-pages/templates/Form";
import { useParseMeta } from "@2u-landing-pages/providers";
import { ContentfulImageConfig } from "@2u-landing-pages/types";
import { getGradientOverlayStyle } from "@2u-landing-pages/utils/gradient-overlay-config";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Container, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { renderEmbeddedEntry, renderMark, renderText } from "../../../../utils/contentful-richtext";
import type { CustomCSSProperties, HeroProps } from "../../Hero.types";
// @ts-ignore
import styles from "./Default.module.scss";

const componentsMap = {
  bento: Bento,
  form: Form,
};

export const Default: React.FC<HeroProps> = ({
  id,
  inspector,
  main_content,
  right_slot,
  bottom_slot,
  background_image,
  image_config,
  programLogo,
  divider_color,
  phoneNumber,
  isLoading,
  show_divider_line,
}) => {
  const parseMetadata = useParseMeta();
  const courseName = main_content?.fields?.eyebrow || "";
  const mainContent = {
    ...main_content,
    fields: {
      ...main_content?.fields,
      eyebrow: undefined, // remove the eyebrow which is displayed in the top slot.
    },
  };
  const hasBottomSlot = bottom_slot ? Boolean(componentsMap[bottom_slot?.sys?.contentType?.sys?.id || ""]) : false;
  const style: CustomCSSProperties = {};

  if (divider_color) style["--hero-divider-color"] = `var(${divider_color})`;

  const backgroundImageUrl = background_image?.fields?.file?.url;
  const imageConfig = image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;

  if (desktopCfg?.focalPoint) style["--focal-point-desktop"] = desktopCfg.focalPoint;
  if (mobileCfg?.focalPoint) style["--focal-point-mobile"] = mobileCfg.focalPoint;

  const desktopOverlayStyle = desktopCfg ? getGradientOverlayStyle(desktopCfg, "desktop") : {};
  const mobileOverlayStyle = mobileCfg ? getGradientOverlayStyle(mobileCfg, "mobile") : {};
  Object.assign(style, desktopOverlayStyle, mobileOverlayStyle);

  return (
    <TemplateWrapper
      className={cn(styles.heroTemplateWrapper, {
        [styles.heroWrapper]: hasBottomSlot,
      })}
      style={style}
    >
      <div
        className={cn(styles.heroTopWrapper, {
          [styles.bottomOverlap]: hasBottomSlot,
          [styles.solidOverlay]: desktopCfg?.gradientType === "solid",
          [styles.gradientOverlay]: desktopCfg?.gradientType === "gradient",
        })}
      >
        {backgroundImageUrl && (
          <div
            className={cn(styles.heroImageWrapper, {
              [styles.solidOverlay]: mobileCfg?.gradientType === "solid",
              [styles.gradientOverlay]: mobileCfg?.gradientType === "gradient",
            })}
          >
            <ContentfulImage
              image={background_image}
              config={imageConfig}
              priority
              fill
              component="hero background"
              className={cn(styles.heroTopBackgroundImage, {
                [styles.flipImageDesktop]: desktopCfg?.flipImage,
                [styles.flipImageMobile]: mobileCfg?.flipImage,
              })}
              {...(id ? inspector({ entryId: id, fieldId: "background_image" }) : {})}
            />
          </div>
        )}
        <Container
          className={styles.heroTop}
          flex
          direction="column"
          lg={{ direction: "row", alignItems: "flex-start" }}
        >
          <div className={cn(styles.mainSlot, { [styles.mainSlotSolo]: !right_slot })}>
            <ContentLayout
              topSlot={
                <div
                  className={cn(styles.courseInfo, {
                    [styles.courseDivider]: Boolean(courseName) || show_divider_line,
                  })}
                >
                  <ContentfulImage image={programLogo} className={styles.logo} width={400} priority />
                  {courseName &&
                    typeof courseName === "object" &&
                    documentToReactComponents(courseName, {
                      renderMark,
                      renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                      renderNode: {
                        [BLOCKS.PARAGRAPH]: (node, children) => (
                          <h1 className={styles.eyebrowWrapper}>
                            <Subhead
                              variant="subhead-md"
                              type="span"
                              className={cn(styles.primaryText, styles.courseName)}
                              style={
                                main_content?.fields?.eyebrow_color
                                  ? { color: `var(${main_content.fields.eyebrow_color})` }
                                  : {}
                              }
                              {...inspector({
                                entryId: main_content?.sys?.id || "",
                                fieldId: "eyebrow",
                              })}
                            >
                              {children}
                            </Subhead>
                          </h1>
                        ),
                        [INLINES.EMBEDDED_ENTRY]: (node) =>
                          renderEmbeddedEntry(node, "Hero", courseName, phoneNumber, parseMetadata),
                      },
                    })}
                </div>
              }
              content={mainContent as HeroProps["main_content"]}
              heading={{
                variant: courseName ? "heading-xl" : "heading-lg",
                className: styles.mainContentHeading,
              }}
              btnPlacement="center"
              phoneNumber={phoneNumber}
            />
          </div>
          {right_slot && componentsMap[right_slot?.sys?.contentType?.sys?.id] && (
            <div className={styles.rightSlot}>
              {React.createElement(componentsMap[right_slot?.sys?.contentType?.sys?.id], {
                ...right_slot?.fields,
                embedded: true,
                phoneNumber,
                isLoading,
                location: "Hero",
              })}
            </div>
          )}
        </Container>
      </div>
      {hasBottomSlot && bottom_slot && (
        <div className={styles.heroBottomWrapper}>
          {React.createElement(componentsMap[bottom_slot?.sys?.contentType?.sys?.id], {
            ...bottom_slot?.fields,
            embedded: true,
            className: styles.heroBottom,
            phoneNumber,
            inspector,
          })}
        </div>
      )}
    </TemplateWrapper>
  );
};
